import { AuraType, Power, PowerEffect } from "../types/game";

export const AURA_COLORS: Record<AuraType, string> = {
  ROUGE: "#FF3B47",
  BLEUE: "#3B82F6",
  VERTE: "#2DE36B",
};

export const AURA_LABELS: Record<AuraType, string> = {
  ROUGE: "Attack",
  BLEUE: "Defense",
  VERTE: "Collective",
};

export interface RecipeMeta {
  name: string;
  description: string;
  effectType: PowerEffect;
  value: number;
  /** XP (points de paris) requis pour atteindre le niveau 2 ; +80 par sort. */
  baseXp: number;
}

// Keys are always sorted alphabetically (BLEUE < ROUGE < VERTE) so both
// craftPower('ROUGE','BLEUE') and craftPower('BLEUE','ROUGE') resolve to
// the same 'BLEUE+ROUGE' key.
export const CRAFTING_RECIPES: Record<string, RecipeMeta> = {
  "ROUGE+ROUGE": {
    name: "Cannonball",
    description: "+15% shooting precision during chances.",
    effectType: "precision",
    value: 0.15,
    baseXp: 360,
  },
  "BLEUE+BLEUE": {
    name: "Lockdown",
    description: "-50% chance of committing a foul when tackling.",
    effectType: "tackle_safety",
    value: 0.5,
    baseXp: 440,
  },
  "VERTE+VERTE": {
    name: "Brick Wall",
    description: "+15% stat bonus for the Bot Goalkeeper.",
    effectType: "goalkeeper_buff",
    value: 0.15,
    baseXp: 520,
  },
  "BLEUE+ROUGE": {
    name: "Lightning Counter",
    description: "+20% chance of counterattack if the opponent misses a shot.",
    effectType: "counter_chance",
    value: 0.2,
    baseXp: 600,
  },
  "ROUGE+VERTE": {
    name: "Triangle Play",
    description: "+10% chance of reaching the opponent's penalty area.",
    effectType: "build_up",
    value: 0.1,
    baseXp: 680,
  },
  "BLEUE+VERTE": {
    name: "Bus",
    description: "Reduces the opponent's overall attack power by 10%.",
    effectType: "enemy_debuff",
    value: 0.1,
    baseXp: 760,
  },
};

/* ─── Niveaux de sorts ───────────────────────────────────────────────────────
   Le niveau d'un sort dérive du total de points de paris (XP). Chaque sort a son
   propre `baseXp` (XP pour le niveau 2) ; ensuite chaque niveau coûte LEVEL_STEP
   de plus. Chaque niveau ajoute LEVEL_VALUE_BONUS (+0,5 pt de %) à l'effet.     */
export const LEVEL_STEP = 500; // XP additionnels par niveau au-delà du niveau 2
export const LEVEL_VALUE_BONUS = 0.005; // +0,5 % d'effet par niveau

/** XP nécessaires pour atteindre `targetLevel` (formule fournie). */
export function pointsForLevel(baseXp: number, targetLevel: number): number {
  if (targetLevel <= 1) return 0;
  return baseXp + LEVEL_STEP * (targetLevel - 2);
}

/** Niveau atteint pour un `baseXp` donné, selon les points (XP). */
export function recipeLevel(baseXp: number, points: number): number {
  if (points < baseXp) return 1;
  return 2 + Math.floor((points - baseXp) / LEVEL_STEP);
}

/** Valeur d'effet à un niveau donné (+0,5 pt de % par niveau). */
export function scaleValue(base: number, level: number): number {
  return +(base + (level - 1) * LEVEL_VALUE_BONUS).toFixed(4);
}

/** Description d'un Power existant — recalculée depuis sa valeur réelle (ignore le champ description stocké). */
export function powerDesc(power: { effectType: PowerEffect; value: number }): string {
  return describeEffect(power.effectType, power.value);
}

/** Génère la description avec le vrai pourcentage selon la valeur scalée. */
export function describeEffect(effectType: PowerEffect, value: number): string {
  const p = Math.round(value * 1000) / 10; // ex: 0.155 → 15.5
  switch (effectType) {
    case "precision":       return `+${p}% shooting precision during chances.`;
    case "tackle_safety":   return `-${p}% chance of committing a foul when tackling.`;
    case "goalkeeper_buff": return `+${p}% stat bonus for the Bot Goalkeeper.`;
    case "counter_chance":  return `+${p}% chance of counterattack if the opponent misses a shot.`;
    case "build_up":        return `+${p}% chance of reaching the opponent's penalty area.`;
    case "enemy_debuff":    return `Reduces the opponent's overall attack power by ${p}%.`;
    default:                return `Effect: ${p}%`;
  }
}

export function craftPower(
  auraA: AuraType,
  auraB: AuraType,
  points = 0
): Power | null {
  const key = [auraA, auraB].sort().join("+");
  const recipe = CRAFTING_RECIPES[key];
  if (!recipe) return null;
  const level = recipeLevel(recipe.baseXp, points);
  const value = scaleValue(recipe.value, level);
  return {
    id: `power_${Math.random().toString(36).slice(2, 9)}`,
    name: recipe.name,
    description: describeEffect(recipe.effectType, value),
    effectType: recipe.effectType,
    value,
    level,
  };
}

/* ─── Recettes spéciales (déblocables aux points de paris) ───────────────────
   Verrouillées tant que le joueur n'a pas atteint le palier `unlockPoints`.
   Une fois débloquées, elles se craftent avec un coût en orbes (combos x3).   */

const orbCost = (rouge = 0, bleue = 0, verte = 0): Record<AuraType, number> => ({
  ROUGE: rouge,
  BLEUE: bleue,
  VERTE: verte,
});

export interface PremiumRecipe {
  id: string;
  name: string;
  description: string;
  effectType: PowerEffect;
  value: number;
  cost: Record<AuraType, number>;
  unlockPoints: number;
  /** XP requis pour le niveau 2 (suite de la progression des sorts). */
  baseXp: number;
}

export const PREMIUM_RECIPES: PremiumRecipe[] = [
  {
    id: "frappe_surpuissante",
    name: "Power Strike",
    description: "+30% shooting precision during chances.",
    effectType: "precision",
    value: 0.3,
    cost: orbCost(3, 0, 0),
    unlockPoints: 1000,
    baseXp: 840,
  },
  {
    id: "forteresse",
    name: "Fortress",
    description: "-75% chance of committing a foul when tackling.",
    effectType: "tackle_safety",
    value: 0.75,
    cost: orbCost(0, 3, 0),
    unlockPoints: 2000,
    baseXp: 920,
  },
  {
    id: "gant_d_or",
    name: "Golden Glove",
    description: "+30% stat bonus for the Bot Goalkeeper.",
    effectType: "goalkeeper_buff",
    value: 0.3,
    cost: orbCost(0, 0, 3),
    unlockPoints: 3000,
    baseXp: 1000,
  },
  {
    id: "contre_fatal",
    name: "Fatal Counter",
    description: "+40% chance of counterattack if the opponent misses a shot.",
    effectType: "counter_chance",
    value: 0.4,
    cost: orbCost(2, 1, 0),
    unlockPoints: 4000,
    baseXp: 1080,
  },
  {
    id: "pressing_total",
    name: "Total Press",
    description: "Reduces the opponent's overall attack power by 25%.",
    effectType: "enemy_debuff",
    value: 0.25,
    cost: orbCost(1, 1, 1),
    unlockPoints: 5000,
    baseXp: 1160,
  },
];

export function makePremiumPower(recipe: PremiumRecipe, points = 0): Power {
  const level = recipeLevel(recipe.baseXp, points);
  const value = scaleValue(recipe.value, level);
  return {
    id: `power_${Math.random().toString(36).slice(2, 9)}`,
    name: recipe.name,
    description: describeEffect(recipe.effectType, value),
    effectType: recipe.effectType,
    value,
    level,
  };
}
